import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import Drawer from '~/components/Drawer';
import moment from 'moment';
import { Link } from 'react-router-dom';
import { MdLock } from 'react-icons/md';
import { formatCurrency } from '~/common/form/Inputs';
import { buildStripePaymentUrl, isStripeLookupId } from '~/common/stripeUrl';
import { nxtGiftUrl } from '~/common/nxtUrl';
import { formatPaymentSource } from '~/common/paymentSource';
import { fetchRefundHistory } from '~/services/refund';
import {
    deleteSupporter,
    getDonationEditHistory,
    searchCampaigns,
    updateDonation,
    UpdateDonationFields,
} from '~/services/campaign';
import RefundModal from '../modals/RefundModal';
import DeleteDonationConfirmationModal from '../modals/DeleteDonationConfirmationModal';
import { RefundSummary } from '../RefundBadge';

const GIFT_CODES = ['LRCC', 'LRCCNF', 'LUCC', 'LUCCNF'];

interface CampaignSearchResult {
    id: number;
    ntaf_id?: string;
    fund_name?: string;
}

export interface DonationDetail {
    id?: number | null;
    name?: string;
    first_name?: string;
    last_name?: string;
    gift_date?: string | null;
    gift_amount?: number | string | null;
    contribution?: number | string | null;
    donation_type?: string;
    payment_method?: string;
    covered_fees?: number;
    donor_share_amount?: number;
    donor_is_anonymous?: number;
    gift_message?: string;
    email?: string;
    address_1?: string;
    address_2?: string;
    city_state?: string;
    state?: string;
    zip_code?: string;
    country?: string;
    stripe_charge_id?: string;
    payout_id?: string;
    gift_id?: number | string | null;
    lookup_id?: string;
    ntaf_id?: string;
    campaign_id?: number | null;
    campaign_name?: string;
    created_date?: string | null;
    date_sync?: string | null;
    gift_code?: string;
    refund_summary?: RefundSummary | null;
    event_name?: string | null;
    mip_locked?: boolean;
    mip_synced_at?: string | null;
    /**
     * True when a full refund deleted the donation row and this record was
     * rebuilt from the refund and the Stripe charge. Read-only: there is no
     * donation left to edit, reassign, delete or refund again.
     */
    donation_deleted?: boolean;
}

interface Props {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    donation: DonationDetail | null;
}

const fieldLabel = 'text-[12px]/[16px] uppercase tracking-wide text-[#949599]';
const sectionHeader =
    'mt-[24px] mb-[12px] text-[14px]/[18px] font-bold uppercase tracking-wide text-black';

const formatType = (value?: string) =>
    value ? value.charAt(0).toUpperCase() + value.slice(1) : '';

// Human label for a fund in the edit-history trail: the general fund for NTAF,
// otherwise the campaign name (falling back to the raw NTAF id).
const fundHistoryLabel = (ntaf?: string, campaignName?: string) =>
    !ntaf || ntaf.toUpperCase() === 'NTAF'
        ? 'General Operating Fund'
        : campaignName || ntaf;

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({
    label,
    children,
}) => (
    <div className='mb-[16px]'>
        <label className={fieldLabel}>{label}</label>
        <div className='text-[14px]/[20px] text-black'>
            {children === '' || children === null || children === undefined
                ? '—'
                : children}
        </div>
    </div>
);

const DonationDetailDrawer: React.FC<Props> = ({
    isOpen,
    setIsOpen,
    donation,
}) => {
    // Hooks must run on every render in the same order, so they all live above
    // the early return below — `donation` can be null while the drawer is empty,
    // so they read it defensively.

    // The editable fields are collected in a single `form` and committed behind
    // one Save button. `seed` is the original (server) snapshot used to compute
    // which fields actually changed so we only send the diff. Re-sync both when a
    // different donation is opened.
    const buildSeed = (d: DonationDetail | null) => ({
        donor_is_anonymous: !!d?.donor_is_anonymous,
        donor_share_amount: !!d?.donor_share_amount,
        ntaf_id: d?.ntaf_id ?? '',
        campaign_id: d?.campaign_id ?? null,
        gift_code: d?.gift_code ?? '',
    });
    const [form, setForm] = useState(() => buildSeed(donation));
    const [seed, setSeed] = useState(() => buildSeed(donation));
    const [saving, setSaving] = useState(false);
    // Display label for the currently selected fund (campaign name). Seeded from
    // the donation and updated when the admin picks a different campaign.
    const [campaignLabel, setCampaignLabel] = useState(
        donation?.campaign_name ?? ''
    );
    const [campaignSearch, setCampaignSearch] = useState('');
    const [campaignResults, setCampaignResults] = useState<
        CampaignSearchResult[]
    >([]);
    const [campaignSearching, setCampaignSearching] = useState(false);
    const [campaignFocused, setCampaignFocused] = useState(false);

    useEffect(() => {
        const next = buildSeed(donation);
        setForm(next);
        setSeed(next);
        setCampaignLabel(donation?.campaign_name ?? '');
        setCampaignSearch('');
        setCampaignResults([]);
        // Keyed on lookup_id as well as id: a donation whose row a full refund
        // deleted has no id, so id alone can't tell two of them apart.
    }, [donation?.id, donation?.lookup_id]);

    // Debounced campaign typeahead, filtered to campaigns that have an NTAF id.
    useEffect(() => {
        const term = campaignSearch.trim();
        if (!term) {
            setCampaignResults([]);
            return;
        }
        let cancelled = false;
        setCampaignSearching(true);
        const handle = setTimeout(async () => {
            try {
                const res = await searchCampaigns(term, true);
                if (!cancelled) setCampaignResults(res?.results ?? []);
            } catch {
                if (!cancelled) setCampaignResults([]);
            } finally {
                if (!cancelled) setCampaignSearching(false);
            }
        }, 300);
        return () => {
            cancelled = true;
            clearTimeout(handle);
        };
    }, [campaignSearch]);

    const [refundOpen, setRefundOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);

    const queryClient = useQueryClient();
    const deleteMutation = useMutation({
        mutationFn: (id: number) => deleteSupporter(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['campaign-supporters'] });
            queryClient.invalidateQueries({ queryKey: ['all-supporters'] });
            // The refund lists link each row back to its donation row, so a
            // delete has to drop that link too — otherwise the Detail action
            // still points at a record that is gone.
            queryClient.invalidateQueries({ queryKey: ['all-refunds'] });
            queryClient.invalidateQueries({ queryKey: ['campaign-refunds'] });
            toast.success('Donation record deleted');
            setDeleteOpen(false);
            setIsOpen(false);
        },
        onError: (error: any) => {
            toast.error(error?.message || 'Failed to delete donation record');
        },
    });

    const refundHistory = useQuery({
        queryKey: ['refund-history', donation?.lookup_id],
        queryFn: () => fetchRefundHistory(donation!.lookup_id!),
        enabled: !!donation?.lookup_id,
        staleTime: 30_000,
    });

    // Fund/gift-code edit audit trail for this donation. Fetched by loc id so it
    // works from any entry point (contributions list or Payout Detail).
    const editHistory = useQuery({
        queryKey: ['donation-edit-history', donation?.id],
        queryFn: () => getDonationEditHistory(donation!.id as number),
        enabled: typeof donation?.id === 'number',
        staleTime: 30_000,
    });

    if (!donation) {
        return (
            <Drawer isOpen={isOpen} setIsOpen={setIsOpen} title='Donation'>
                <div />
            </Drawer>
        );
    }

    // Admin view shows the donor's real name even when anonymous (for lookup);
    // the anonymity state is surfaced separately via a badge and the Privacy
    // section. Public/client views mask the name server-side.
    const displayName =
        donation.name ||
        [donation.first_name, donation.last_name].filter(Boolean).join(' ');

    const amountRaw =
        donation.contribution ?? donation.gift_amount ?? null;
    const amount =
        amountRaw === null || amountRaw === '' ? null : Number(amountRaw);
    const isPrivate = !form.donor_share_amount;
    const nameAnonymous = form.donor_is_anonymous;

    const addressLines = [
        donation.address_1,
        donation.address_2,
        [donation.city_state, donation.state].filter(Boolean).join(', '),
        donation.zip_code,
        donation.country,
    ].filter(Boolean);

    const userRoles: string[] = (() => {
        try {
            const raw = localStorage.getItem('user');
            if (!raw) return [];
            const parsed = JSON.parse(raw);
            return parsed.roles || [];
        } catch {
            return [];
        }
    })();
    // Refund is available when we have any Stripe identifier: either a
    // stripe_charge_id (donation synced to NXT) or a Stripe-shaped lookup_id
    // (pi_* payment intent, or ch_*/py_* charge — set on all WordPress Stripe
    // donations regardless of NXT sync state). py_ charges come from non-card
    // payment methods (e.g. bank/ACH) and are refundable just like ch_.
    const hasStripeReference =
        !!donation.stripe_charge_id || isStripeLookupId(donation.lookup_id);
    // Not offered on a record rebuilt after a full refund: everything was
    // already returned, and the balance check that would otherwise catch it
    // only lands once the refund history has loaded.
    const canRefund =
        hasStripeReference &&
        !!donation.lookup_id &&
        !donation.donation_deleted &&
        (userRoles.includes('administrator') || userRoles.includes('Financial'));

    const canDelete =
        typeof donation.id === 'number' &&
        (userRoles.includes('administrator') || userRoles.includes('Financial'));

    const canEditAnonymity =
        typeof donation.id === 'number' &&
        (userRoles.includes('administrator') || userRoles.includes('Financial'));

    const stripeReferenceId = donation.stripe_charge_id || donation.lookup_id;
    const stripeUrl = hasStripeReference
        ? buildStripePaymentUrl(stripeReferenceId)
        : null;

    // gift_id is the Blackbaud Raiser's Edge NXT gift system record id, set once
    // the donation has synced to NXT. Use it to deep-link to the gift in NXT.
    const nxtUrl = donation.gift_id
        ? nxtGiftUrl(String(donation.gift_id))
        : null;

    // Status only applies to Stripe transactions (matches the one-time
    // contributions table): Approved once an NXT gift exists, else Pending.
    // Also gates the raw lookup_id's Stripe link further down.
    const isStripeTransaction = isStripeLookupId(donation.lookup_id);
    const isApproved = donation.gift_id != null;

    // Once a donation has posted to MIP its accounting records are immutable
    // there, so edits are blocked (enforced server-side too).
    const mipLocked = !!donation.mip_locked;
    const mipSyncedAt = donation.mip_synced_at;

    // A full refund deletes the donation row, so this record was rebuilt from
    // the refund and the Stripe charge (opened from a refunds list). Everything
    // is history at that point: there is no row to reassign, edit or delete,
    // and nothing left to refund. Read-only for the same reason as mipLocked,
    // which is why it rides along with it through the editing controls.
    const donationDeleted = !!donation.donation_deleted;
    const notEditable = mipLocked || donationDeleted;

    // Tips are a separate unrestricted gift to HHL's general fund; their loc row
    // is keyed with a `-TIP`/`_tip` lookup suffix. They can't be reassigned to a
    // campaign fund or have their gift code changed (enforced server-side too).
    const isTip = /[-_]tip$/i.test(donation.lookup_id ?? '');

    // The payout processor sets gift_code and the NXT gift id in the same save,
    // so a Stripe donation with no gift_id yet can't have a code — offering the
    // dropdown just invites a hand-picked value the sync will overwrite.
    // Deliberately keyed off gift_id, not payout_id: payout_id is only attached
    // by the all-supporters and single-donation endpoints, so it's absent on the
    // campaign donor lists that also open this drawer. Manual (check/offline)
    // donations never get a gift_id at all, hence the Stripe-only scope. Once a
    // gift_id exists the field stays editable even if the code came back blank —
    // that means the sync ran and failed, which is when staff need to fix it.
    const awaitingPayoutSync = isStripeTransaction && !isApproved;

    // The fund selection (ntaf_id + campaign_id) is treated as a unit: it's
    // "changed" when either differs from the seed. The backend contract wants
    // both sent together only when the fund changed.
    const fundChanged =
        form.ntaf_id !== seed.ntaf_id || form.campaign_id !== seed.campaign_id;
    const giftCodeChanged = form.gift_code !== seed.gift_code;
    const anonymousChanged =
        form.donor_is_anonymous !== seed.donor_is_anonymous;
    const shareAmountChanged =
        form.donor_share_amount !== seed.donor_share_amount;
    const isDirty =
        fundChanged ||
        giftCodeChanged ||
        anonymousChanged ||
        shareAmountChanged;

    const handleSave = async () => {
        if (typeof donation.id !== 'number') return;
        const payload: UpdateDonationFields = {};
        if (fundChanged) {
            payload.ntaf_id = form.ntaf_id;
            payload.campaign_id = form.campaign_id;
        }
        if (giftCodeChanged) payload.gift_code = form.gift_code;
        if (anonymousChanged)
            payload.donor_is_anonymous = form.donor_is_anonymous;
        if (shareAmountChanged)
            payload.donor_share_amount = form.donor_share_amount;

        setSaving(true);
        try {
            await updateDonation(donation.id, payload);
            queryClient.invalidateQueries({ queryKey: ['campaign-supporters'] });
            queryClient.invalidateQueries({ queryKey: ['all-supporters'] });
            toast.success('Donation updated');
            setIsOpen(false);
        } catch (err: any) {
            toast.error(err?.message || 'Failed to update donation');
        } finally {
            setSaving(false);
        }
    };

    const isGof = form.ntaf_id === 'NTAF';
    const fundSelectionLabel = isGof
        ? 'General Operating Fund'
        : campaignLabel || form.ntaf_id || '—';

    const refundedTotal = (refundHistory.data?.results || [])
        .filter((r) => r.status === 'succeeded')
        .reduce(
            (sum, r) =>
                sum +
                Number(r.donation_refund_amount) +
                Number(r.tip_refund_amount),
            0
        );
    const remainingRefundable = Number(
        refundHistory.data?.balance?.remaining_refundable || '0'
    );
    const hasAnyRefunds = refundedTotal > 0;
    const isFullyRefunded =
        hasAnyRefunds &&
        refundHistory.data !== undefined &&
        remainingRefundable <= 0;

    return (
        <Drawer
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            title='Donation Detail'
        >
            <div className='rounded bg-[#e4f6f7] p-[16px]'>
                {mipLocked && (
                    <div
                        className='mb-[12px] flex items-start gap-2 rounded border p-[10px] text-[12px]/[16px]'
                        style={{ borderColor: '#FCD34D', backgroundColor: '#FFFBEB', color: '#92400E' }}
                    >
                        <MdLock size={14} className='mt-[1px] shrink-0' />
                        <span>
                            This donation has synced to MIP
                            {mipSyncedAt
                                ? ` on ${moment(mipSyncedAt).format('MMMM D, YYYY')}`
                                : ''}
                            . Edits are locked here — corrections must be made
                            manually in each system.
                        </span>
                    </div>
                )}
                {donationDeleted && (
                    <div
                        className='mb-[12px] flex items-start gap-2 rounded border p-[10px] text-[12px]/[16px]'
                        style={{ borderColor: '#FCA5A5', backgroundColor: '#FEF2F2', color: '#991B1B' }}
                    >
                        <MdLock size={14} className='mt-[1px] shrink-0' />
                        <span>
                            This donation was refunded in full, which removed its
                            record from the contributions list. What follows is
                            rebuilt from the refund and the Stripe charge, so
                            some gift details are unavailable and nothing here
                            can be edited.
                        </span>
                    </div>
                )}
                {isFullyRefunded && (
                    <div
                        className='mb-[12px] inline-block rounded-full px-[12px] py-[3px] text-[11px]/[14px] font-bold uppercase tracking-wide text-white'
                        style={{ backgroundColor: '#b91c1c' }}
                    >
                        Fully Refunded
                    </div>
                )}
                {!isFullyRefunded && hasAnyRefunds && (
                    <div className='mb-[12px] inline-block rounded-full bg-gray-200 px-[12px] py-[3px] text-[11px]/[14px] font-bold uppercase tracking-wide text-gray-700'>
                        Partially Refunded ({formatCurrency(refundedTotal)})
                    </div>
                )}
                <div className='mb-[4px] text-[12px]/[16px] text-[#5d7d7f]'>
                    {donation.gift_date
                        ? moment(donation.gift_date).format('MMMM D, YYYY')
                        : '—'}
                </div>
                <div className='flex items-center gap-2 text-[28px]/[32px] font-bold'>
                    {amount !== null && !Number.isNaN(amount)
                        ? formatCurrency(amount)
                        : 'Amount hidden'}
                    {isPrivate && (
                        <MdLock
                            className='text-gray-400'
                            size={18}
                            title='Donor opted not to share amount'
                        />
                    )}
                </div>
                <div className='mt-[4px] flex items-center gap-2 text-[14px]/[20px] text-black'>
                    {displayName || '—'}
                    {nameAnonymous && (
                        <span
                            className='inline-flex items-center gap-1 rounded-full bg-gray-200 px-[8px] py-[2px] text-[11px]/[14px] font-bold uppercase tracking-wide text-gray-700'
                            title='Donor chose to stay anonymous — name is hidden on public and client-facing views'
                        >
                            <MdLock size={12} />
                            Anonymous
                        </span>
                    )}
                </div>
                {isStripeTransaction && (
                    <div className='mt-[8px]'>
                        <span
                            className='rounded-full px-[10px] py-[3px] text-[11px]/[14px] font-bold uppercase tracking-wide'
                            style={
                                isApproved
                                    ? { backgroundColor: '#DCFCE7', color: '#15803D' }
                                    : { backgroundColor: '#FEF3C7', color: '#B45309' }
                            }
                            title={
                                isApproved
                                    ? 'A linked NXT gift exists for this donation'
                                    : 'Awaiting payout settlement before a gift is created in NXT'
                            }
                        >
                            {isApproved ? 'Approved' : 'Pending Settlement'}
                        </span>
                    </div>
                )}
                {(canRefund && !isFullyRefunded) ||
                canDelete ||
                stripeUrl ||
                nxtUrl ? (
                    <div className='mt-[12px] flex flex-wrap items-center gap-3'>
                        {canRefund && !isFullyRefunded && (
                            <button
                                type='button'
                                onClick={() => setRefundOpen(true)}
                                className='rounded-full border-[2px] border-black px-[24px] py-[6px] text-[14px] font-bold uppercase hover:bg-black hover:text-white'
                            >
                                Refund
                            </button>
                        )}
                        {nxtUrl && (
                            <a
                                href={nxtUrl}
                                target='_blank'
                                rel='noopener noreferrer'
                                className='rounded-full border-[2px] border-black px-[24px] py-[6px] text-[14px] font-bold uppercase hover:bg-black hover:text-white'
                            >
                                View in NXT
                            </a>
                        )}
                        {stripeUrl && (
                            <a
                                href={stripeUrl}
                                target='_blank'
                                rel='noopener noreferrer'
                                className='rounded-full border-[2px] border-black px-[24px] py-[6px] text-[14px] font-bold uppercase hover:bg-black hover:text-white'
                            >
                                View in Stripe
                            </a>
                        )}
                        {canDelete && (
                            <button
                                type='button'
                                onClick={() => setDeleteOpen(true)}
                                className='rounded-full border-[2px] border-danger px-[24px] py-[6px] text-[14px] font-bold uppercase text-danger hover:bg-danger hover:text-white'
                            >
                                Delete
                            </button>
                        )}
                    </div>
                ) : null}
            </div>

            <h2 className={sectionHeader}>Campaign</h2>
            <div className='mb-[16px]'>
                <label className={fieldLabel}>Fund / NTAF</label>
                {notEditable ? (
                    <div className='text-[14px]/[20px] text-black'>
                        {form.campaign_id && campaignLabel ? (
                            <div className='flex items-center gap-3'>
                                <Link
                                    to={`/campaigns/${form.campaign_id}`}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    className='font-bold text-primary'
                                >
                                    {campaignLabel}
                                </Link>
                                <a
                                    href={`${import.meta.env.VITE_CLIENT_URL}/dashboard?campaignId=${form.campaign_id}`}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    className='text-[12px]/[16px] text-primary underline'
                                >
                                    Open client dashboard
                                </a>
                            </div>
                        ) : (
                            fundSelectionLabel
                        )}
                        {form.ntaf_id && !isGof && (
                            <div className='text-[12px]/[16px] text-[#949599]'>
                                NTAF {form.ntaf_id}
                            </div>
                        )}
                    </div>
                ) : (
                    <>
                        <div className='mb-[6px] text-[14px]/[20px] text-black'>
                            Current:{' '}
                            {form.campaign_id && !isGof ? (
                                <Link
                                    to={`/campaigns/${form.campaign_id}`}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    className='font-bold text-primary'
                                >
                                    {fundSelectionLabel}
                                </Link>
                            ) : (
                                <span className='font-bold'>
                                    {fundSelectionLabel}
                                </span>
                            )}
                            {form.ntaf_id && !isGof && (
                                <span className='ml-2 text-[12px]/[16px] text-[#949599]'>
                                    (NTAF {form.ntaf_id})
                                </span>
                            )}
                        </div>
                        {isTip ? (
                            <p className='text-[12px]/[16px] text-[#949599]'>
                                Tips are unrestricted gifts to HHL and can’t be
                                reassigned.
                            </p>
                        ) : (
                            <>
                        <label className={fieldLabel}>Reassign to</label>
                        <div className='relative mt-[2px]'>
                            <input
                                type='text'
                                value={campaignSearch}
                                placeholder='Search campaigns by name or NTAF ID'
                                onChange={(e) =>
                                    setCampaignSearch(e.target.value)
                                }
                                onFocus={() => setCampaignFocused(true)}
                                onBlur={() => setCampaignFocused(false)}
                                className='w-full rounded-lg border border-[#949599] p-[10px] text-[14px]/[20px]'
                            />
                            {campaignFocused && (
                                <div className='absolute z-10 mt-[4px] max-h-[220px] w-full overflow-y-auto rounded-lg border border-[#949599] bg-white shadow-lg'>
                                    <button
                                        type='button'
                                        onMouseDown={(e) => {
                                            e.preventDefault();
                                            setForm((f) => ({
                                                ...f,
                                                ntaf_id: 'NTAF',
                                                campaign_id: null,
                                            }));
                                            setCampaignLabel('');
                                            setCampaignSearch('');
                                            setCampaignResults([]);
                                            setCampaignFocused(false);
                                        }}
                                        className={`flex w-full items-center justify-between gap-3 border-b border-[#e4e4e7] p-[10px] text-left text-[14px]/[18px] hover:bg-[#f4f4f5] ${
                                            isGof ? 'bg-[#f4f4f5] font-bold' : ''
                                        }`}
                                    >
                                        <span>General Operating Fund</span>
                                        <span className='text-[12px]/[16px] text-[#949599]'>
                                            Unrestricted
                                        </span>
                                    </button>
                                    {campaignSearching && (
                                        <div className='p-[10px] text-[12px]/[16px] text-[#949599]'>
                                            Searching…
                                        </div>
                                    )}
                                    {campaignResults.map((c) => (
                                        <button
                                            type='button'
                                            key={c.id}
                                            onMouseDown={(e) => {
                                                e.preventDefault();
                                                setForm((f) => ({
                                                    ...f,
                                                    ntaf_id: c.ntaf_id ?? '',
                                                    campaign_id: c.id,
                                                }));
                                                setCampaignLabel(
                                                    c.fund_name ?? ''
                                                );
                                                setCampaignSearch('');
                                                setCampaignResults([]);
                                                setCampaignFocused(false);
                                            }}
                                            className='flex w-full items-center justify-between gap-3 border-b border-[#e4e4e7] p-[10px] text-left text-[14px]/[18px] last:border-b-0 hover:bg-[#f4f4f5]'
                                        >
                                            <span>{c.fund_name || '—'}</span>
                                            <span className='text-[12px]/[16px] text-[#949599]'>
                                                {c.ntaf_id}
                                            </span>
                                        </button>
                                    ))}
                                    {!campaignSearching &&
                                        campaignSearch.trim() &&
                                        campaignResults.length === 0 && (
                                            <div className='p-[10px] text-[12px]/[16px] text-[#949599]'>
                                                No matching campaigns
                                            </div>
                                        )}
                                </div>
                            )}
                        </div>
                            </>
                        )}
                    </>
                )}
            </div>

            <h2 className={sectionHeader}>Gift</h2>
            <div className='grid grid-cols-2 gap-x-[16px]'>
                <Field label='Type'>{formatType(donation.donation_type)}</Field>
                <Field label='Payment Source'>
                    {formatPaymentSource(donation.payment_method)}
                </Field>
                <Field label='Fees Covered'>
                    {donation.covered_fees ? 'Yes' : 'No'}
                </Field>
                <Field label='Amount Shared'>
                    {form.donor_share_amount ? 'Yes' : 'No'}
                </Field>
            </div>
            <div className='mb-[16px]'>
                <label className={fieldLabel}>Gift Code</label>
                <select
                    value={form.gift_code}
                    disabled={notEditable || isTip || awaitingPayoutSync}
                    onChange={(e) =>
                        setForm((f) => ({ ...f, gift_code: e.target.value }))
                    }
                    className='mt-[2px] w-full rounded-lg border border-[#949599] p-[10px] text-[14px]/[20px] disabled:bg-[#f4f4f5] disabled:text-[#949599]'
                >
                    <option value=''>—</option>
                    {GIFT_CODES.map((code) => (
                        <option key={code} value={code}>
                            {code}
                        </option>
                    ))}
                </select>
                {awaitingPayoutSync && !isTip && (
                    <p className='mt-[4px] text-[12px]/[16px] text-[#949599]'>
                        This is set automatically when the payout is processed.
                    </p>
                )}
            </div>
            {donation.donation_type === 'event' && donation.event_name && (
                <Field label='Event'>{donation.event_name}</Field>
            )}
            {donation.gift_message && (
                <Field label='Message'>
                    <div className='whitespace-pre-wrap'>
                        {donation.gift_message}
                    </div>
                </Field>
            )}

            <h2 className={sectionHeader}>Donor</h2>
            <Field label='Name'>{displayName}</Field>
            <Field label='Email'>
                {donation.email ? (
                    <a
                        className='font-bold text-primary'
                        href={`mailto:${donation.email}`}
                    >
                        {donation.email}
                    </a>
                ) : (
                    ''
                )}
            </Field>
            <Field label='Address'>
                {addressLines.length ? (
                    <div className='whitespace-pre-line'>
                        {addressLines.join('\n')}
                    </div>
                ) : (
                    ''
                )}
            </Field>

            {canEditAnonymity && (
                <>
                    <h2 className={sectionHeader}>Privacy</h2>
                    <div className='flex flex-col gap-3'>
                        <label className='flex items-center gap-2 text-[14px]/[20px] text-black'>
                            <input
                                type='checkbox'
                                checked={form.donor_is_anonymous}
                                disabled={mipLocked}
                                onChange={(e) =>
                                    setForm((f) => ({
                                        ...f,
                                        donor_is_anonymous: e.target.checked,
                                    }))
                                }
                            />
                            Keep donor name anonymous
                        </label>
                        <label className='flex items-center gap-2 text-[14px]/[20px] text-black'>
                            <input
                                type='checkbox'
                                checked={!form.donor_share_amount}
                                disabled={mipLocked}
                                onChange={(e) =>
                                    setForm((f) => ({
                                        ...f,
                                        donor_share_amount: !e.target.checked,
                                    }))
                                }
                            />
                            Hide donation amount
                        </label>
                        {mipLocked ? (
                            <p
                                className='text-[12px]/[16px]'
                                style={{ color: '#B45309' }}
                            >
                                Posted to MIP — no longer editable
                            </p>
                        ) : (
                            <p className='text-[12px]/[16px] text-[#949599]'>
                                Name changes sync to the linked NXT gift when
                                available. Amount visibility affects HHL public
                                display only.
                            </p>
                        )}
                    </div>
                </>
            )}

            <h2 className={sectionHeader}>References</h2>
            <div className='grid grid-cols-2 gap-x-[16px]'>
                <Field label='Gift ID'>
                    {donation.gift_id ? (
                        <a
                            href={nxtUrl || '#'}
                            target='_blank'
                            rel='noopener noreferrer'
                            className='font-bold text-primary'
                        >
                            {donation.gift_id}
                        </a>
                    ) : (
                        ''
                    )}
                </Field>
                <Field label='Lookup ID'>
                    {isStripeTransaction ? (
                        <a
                            href={buildStripePaymentUrl(donation.lookup_id) || '#'}
                            target='_blank'
                            rel='noopener noreferrer'
                            className='font-bold text-primary'
                        >
                            {donation.lookup_id}
                        </a>
                    ) : (
                        donation.lookup_id
                    )}
                </Field>
                <Field label='Stripe Charge'>
                    {donation.stripe_charge_id ? (
                        <a
                            href={buildStripePaymentUrl(donation.stripe_charge_id) || '#'}
                            target='_blank'
                            rel='noopener noreferrer'
                            className='font-bold text-primary'
                        >
                            {donation.stripe_charge_id}
                        </a>
                    ) : (
                        ''
                    )}
                </Field>
                <Field label='Payout'>
                    {donation.payout_id ? (
                        <Link
                            to={`/payouts/${donation.payout_id}`}
                            className='font-bold text-primary'
                        >
                            {donation.payout_id}
                        </Link>
                    ) : (
                        ''
                    )}
                </Field>
            </div>

            <h2 className={sectionHeader}>Sync</h2>
            <div className='grid grid-cols-2 gap-x-[16px]'>
                <Field label='Created'>
                    {donation.created_date
                        ? moment(donation.created_date).format(
                              'MM/DD/YYYY h:mm A'
                          )
                        : ''}
                </Field>
                <Field label='Last Synced'>
                    {donation.date_sync
                        ? moment(donation.date_sync).format(
                              'MM/DD/YYYY h:mm A'
                          )
                        : ''}
                </Field>
            </div>

            {editHistory.data && editHistory.data.length > 0 && (
                <>
                    <h2 className={sectionHeader}>History</h2>
                    {editHistory.data.map((e) => {
                        const fundChanged =
                            e.old_ntaf_id !== e.new_ntaf_id ||
                            e.old_campaign_id !== e.new_campaign_id;
                        const codeChanged =
                            e.old_gift_code !== e.new_gift_code;
                        return (
                            <div
                                key={e.id}
                                className='mb-[12px] rounded border border-stroke p-3 text-[13px]'
                            >
                                {fundChanged && (
                                    <div>
                                        <span className='font-bold'>
                                            Fund:
                                        </span>{' '}
                                        {fundHistoryLabel(
                                            e.old_ntaf_id,
                                            e.old_campaign_name
                                        )}{' '}
                                        →{' '}
                                        {fundHistoryLabel(
                                            e.new_ntaf_id,
                                            e.new_campaign_name
                                        )}
                                    </div>
                                )}
                                {codeChanged && (
                                    <div>
                                        <span className='font-bold'>
                                            Gift code:
                                        </span>{' '}
                                        {e.old_gift_code || '—'} →{' '}
                                        {e.new_gift_code || '—'}
                                    </div>
                                )}
                                <div className='text-[#949599]'>
                                    {e.changed_by || 'Unknown'} ·{' '}
                                    {moment(e.changed_at).format(
                                        'MM/DD/YYYY h:mm A'
                                    )}{' '}
                                    ·{' '}
                                    {e.nxt_recreated
                                        ? 'Synced to NXT'
                                        : 'Local only'}
                                </div>
                            </div>
                        );
                    })}
                </>
            )}

            {refundHistory.data?.results && refundHistory.data.results.length > 0 && (
                <>
                    <h2 className={sectionHeader}>Refund History</h2>
                    {refundHistory.data.results.map((r) => (
                        <div
                            key={r.id}
                            className='mb-[12px] rounded border border-stroke p-3 text-[13px]'
                        >
                            <div className='font-bold'>
                                {formatCurrency(
                                    Number(r.donation_refund_amount) +
                                        Number(r.tip_refund_amount)
                                )}{' '}
                                refunded
                            </div>
                            <div className='text-[#949599]'>
                                {r.refunded_at
                                    ? moment(r.refunded_at).format('MM/DD/YYYY h:mm A')
                                    : ''}{' '}
                                — {r.status}
                            </div>
                            {r.status === 'succeeded' && (
                                <div className='mt-1 flex items-center gap-2'>
                                    <span
                                        className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-medium capitalize ${
                                            r.completion_status === 'complete'
                                                ? 'bg-success text-white'
                                                : 'bg-warning text-black'
                                        }`}
                                        title='Whether finance has hand-entered this refund into Raiser’s Edge and MIP'
                                    >
                                        RE + MIP: {r.completion_status || 'pending'}
                                    </span>
                                    {r.completion_status === 'complete' &&
                                        r.completed_at && (
                                            <span className='text-[11px] text-[#949599]'>
                                                {moment(r.completed_at).format(
                                                    'MM/DD/YYYY'
                                                )}
                                            </span>
                                        )}
                                </div>
                            )}
                            {/* Stripe has no per-refund page; a refund is reviewed on
                                its payment. Show the id as text to search on and send
                                the link where it actually goes. */}
                            {r.stripe_refund_id && (
                                <div className='mt-1'>
                                    <span className='text-[#949599]'>Refund ID: </span>
                                    {r.stripe_refund_id}
                                    {donation.lookup_id && (
                                        <>
                                            {' · '}
                                            <a
                                                href={
                                                    buildStripePaymentUrl(
                                                        donation.lookup_id
                                                    ) || '#'
                                                }
                                                target='_blank'
                                                rel='noopener noreferrer'
                                                className='font-bold text-primary'
                                            >
                                                View payment in Stripe
                                            </a>
                                        </>
                                    )}
                                </div>
                            )}
                            {r.failure_reason && (
                                <div className='text-red-600'>{r.failure_reason}</div>
                            )}
                        </div>
                    ))}
                </>
            )}

            {/* Nothing is editable on a deleted donation, so a Save button
                could only ever sit there disabled. */}
            {!donationDeleted && (
                <div className='mt-[24px] flex justify-end border-t border-stroke pt-[16px]'>
                    <button
                        type='button'
                        onClick={handleSave}
                        disabled={!isDirty || notEditable || saving}
                        className='rounded-full bg-primary px-[32px] py-[8px] text-[14px] font-bold uppercase text-white disabled:cursor-not-allowed disabled:opacity-50'
                    >
                        {saving ? 'Saving…' : 'Save'}
                    </button>
                </div>
            )}

            <RefundModal
                isOpen={refundOpen}
                lookupId={donation.lookup_id || null}
                donorName={displayName || ''}
                onClose={() => {
                    setRefundOpen(false);
                    refundHistory.refetch();
                }}
            />

            <DeleteDonationConfirmationModal
                isOpen={deleteOpen}
                onClose={() => {
                    if (!deleteMutation.isPending) setDeleteOpen(false);
                }}
                onConfirm={() => {
                    if (typeof donation.id === 'number') {
                        deleteMutation.mutate(donation.id);
                    }
                }}
                isLoading={deleteMutation.isPending}
                donorName={displayName || ''}
            />
        </Drawer>
    );
};

export default DonationDetailDrawer;
